import { useContext, useEffect, useMemo } from 'react';
import { useRouterParams } from 'src/hooks/utils';
import {
  useJob,
  useJobRelation,
  useChecklistQuestionResult,
  useDatasheetQuestionResult,
} from 'src/hooks/job';
import { useCompanyTypes } from 'src/hooks/companyType';
import { useReportReview } from 'src/hooks/reportReview';
import { useResultHistories } from 'src/hooks/resultHistory';
import { useCapFindings } from 'src/hooks/cap';
import { userAuthorization } from 'src/utils/auth';
import camelize from 'camelize';
import CurrentUserContext from 'src/utils/context/CurrentUserContext';
import CurrentUserPermissionsContext from 'src/utils/context/CurrentUserPermissionsContext';
import {
  READY_FOR_INTERNAL_SAQ,
  UNDER_INTERNAL_SAQ,
  UNDER_EXTERNAL_SAQ,
} from 'src/utils/data/job';
import { ServiceTypes } from 'src/utils/data/types';
import { useExternalJobStakeholders } from 'src/hooks/jobExternalStakeholder';

export default match => {
  const auth = userAuthorization();
  const currentUser = useContext(CurrentUserContext);
  const { permissions } = useContext(CurrentUserPermissionsContext);

  const { isNew, id } = useRouterParams(match);
  const { data: jobData, loading: jobloading, refetch: reloadJob } = useJob(
    id,
    isNew,
  );
  const { data: jobRelationData, loading: jobRelationloading, refetch: refetchJobRelation } = useJobRelation(
    id,
    isNew,
  );
  let job =
    jobData && jobRelationData
      ? { ...jobRelationData.job, ...jobData.job }
      : {};
  const prepareJobData = jData => {
    if (jData && jData.jobServicesJson) {
      return {
        ...jData,
        jobServicesJson: null,
        jobServices: camelize(jData.jobServicesJson),
      };
    }

    return jData;
  };

  const [
    fetchFullJobData,
    { data: fullJobData, loading: fullJobLoading },
  ] = useJobRelation(id, isNew, false, false);
  const fullJob = (fullJobData && prepareJobData(fullJobData.job)) || [];
  job = { ...job, ...fullJob };

  useEffect(() => {
    fetchFullJobData({
      variables: {
        id,
      },
    });
  }, [JSON.stringify(((jobRelationData && jobRelationData.job) || {}).id)]);

  const {
    data: companyTypesData,
    loading: companyTypesLoading,
  } = useCompanyTypes({
    offset: 0,
    limit: 1000,
  });

  const jobMeta = jobData ? jobData.job : {};

  const companyTypes =
    (companyTypesData && companyTypesData.companyTypes) || [];

  const [
    getReportReview,
    {
      data: reportReviewData,
      loading: reportReviewDataLoading,
      refetch: refetchReportReview,
    },
  ] = useReportReview({});

  const [
    getResultHistories,
    {
      data: resultHistories,
      loading: resultHistoriesDataLoading,
      refetch: refetchResultHistories,
    },
  ] = useResultHistories({});

  useEffect(() => {
    if (!jobRelationloading && jobRelationData?.job?.id) {
      getReportReview({ variables: { jobId: jobRelationData.job.id } });
    }
  }, [jobRelationloading, jobRelationData?.job?.id]);

  const dontHaveAccessToAnswers = auth.isFactoryUser && !auth.isAdmin && !auth.isAuditor && !auth.isReportReviewer;
  const {
    data: checklistResultsData,
    refetch: reloadJobChecklistResults,
  } = useChecklistQuestionResult(id, isNew);
  const {
    data: datasheetResultsData,
    refetch: reloadJobDatasheetResults,
    loading: loadingDatasheetResultsData
  } = useDatasheetQuestionResult(id, isNew);

  const isDatasheetResultsLoaded = datasheetResultsData !== undefined;
  let jobChecklistResults = (checklistResultsData && checklistResultsData?.jobWithChecklistQuestionResults.checklistQuestionResults) || [];

  const checklistIds = jobChecklistResults.reduce((acc, checklist) => {
    if (!acc.includes(checklist.checklistId)) {
      acc.push(checklist.checklistId);
    }
    return acc;
  }, []);

  jobChecklistResults = checklistIds.map(x => {
    return {
      checklist_id: x,
      data: jobChecklistResults
        .filter(y => y.checklistId === x)
        .map(z => {
          const result = z;
          if (z.result && typeof z.result === 'string') {
            result.result = JSON.parse(
              z.result.replace(/=>/g, ': ').replace(/nil/g, 'null'),
            );
          }
          return result;
        }),
    };
  });

  let jobDatasheetResults = useMemo(()=> (datasheetResultsData && datasheetResultsData?.jobWithDatasheetQuestionResults?.datasheetQuestionResults) || [],
    [loadingDatasheetResultsData]);

  const datasheetIds = jobDatasheetResults.reduce((acc, datasheet) => {
    if (!acc.includes(datasheet.datasheetId)) {
      acc.push(datasheet.datasheetId);
    }
    return acc;
  }, []);

  jobDatasheetResults = datasheetIds.map(x => {
    return {
      datasheet_id: x,
      data: jobDatasheetResults
        .filter(y => y.datasheetId === x)
        .map(z => {
          const result = { ...z };
          if (z.result && typeof z.result === 'string') {
            result.result = JSON.parse(
              z.result.replace(/=>/g, ': ').replace(/nil/g, 'null'),
            );
            result.result.validationResultId = result.validationResultId;
            // result.commentStatus = 'open';
          }
          return result;
        }),
    };
  });

  const isInternalUser = auth.isAdmin ||
    auth.isCapManager ||
    auth.isReportReviewer ||
    auth.isAuditor;

  const isJobAuditor = (jobDetails, userId) => {
    return (jobDetails?.jobAuditors || []).findIndex(({ user }) => String(user.id) === userId) !== -1;
  };

  const isJobReportReviewer = (jobDetails, userId) => {
    /**
     * user other than job auditors is considered as reviewer if it has one of internal roles
     * and can view this job in review
     */
    return !isJobAuditor(jobDetails, userId) && isInternalUser;
  };

  const isReportNotesAvailableToAuditor = [
    UNDER_EXTERNAL_SAQ,
    READY_FOR_INTERNAL_SAQ,
    UNDER_INTERNAL_SAQ,
  ].includes(job?.status);

  const notAnswered = (comments, currentUserIsReviewer) => {
    if (comments.length === 0) {
      return !isReportNotesAvailableToAuditor && !currentUserIsReviewer;
    }

    const newestComment = [...comments].sort(c => new Date(c.createdAt))[
      comments.length - 1
    ];

    const { user: commentUser } = newestComment || {};

    const commentUserIsReportReviewer = isJobReportReviewer(job, commentUser?.id);

    return currentUserIsReviewer !== commentUserIsReportReviewer;
  };

  const notAnsweredByExternalUser = (comments) => {
    if (comments.length === 0) {
      return true;
    }

    const newestComment = [...comments].sort(c => new Date(c.createdAt))[
      comments.length - 1
    ];

    const { user: commentUser } = newestComment || {};

    return isJobAuditor(job, commentUser?.id);
  };

  const actionItemExistedNotes = (
    notes,
    type,
    typeId,
    currentUserIsReviewer,
  ) => {
    const filteredNotes = notes.filter(
      n => n.type === type && n.typeId === +typeId,
    );
    return filteredNotes.filter(
      n =>
        !n.closedAt &&
        notAnswered(n.reportReviewComments, currentUserIsReviewer),
    );
  };

  const getExternalUserActionRequiredNotes = (
    notes,
    type,
    typeId,
  ) => {
    const filteredNotes = notes.filter(
      n => n.type === type && n.typeId === +typeId,
    );
    return filteredNotes.filter(
      n =>
        !n.closedAt &&
        notAnsweredByExternalUser(n.reportReviewComments),
    );
  };

  const actionNotRequiredNotes = (
    notes,
    type,
    typeId,
    currentUserIsReviewer,
  ) => {
    const filteredNotes = notes.filter(
      n => n.type === type && n.typeId === +typeId,
    );
    return filteredNotes.filter(
      n =>
        n.closedAt ||
        !notAnswered(n.reportReviewComments, currentUserIsReviewer),
    );
  };

  let reportReviewStatistics = [];
  let allNoteCount = null;
  let closedNoteCount = null;

  const currentUserIsJobReviewer =
    jobData &&
    reportReviewData &&
    isJobReportReviewer(job, currentUser?.id);

  if (reportReviewData) {
    let notes = reportReviewData.ReportReview.reportReviewNotes;
    if (!isInternalUser) {
      notes = notes.filter(c => c.isExternal);
    }
    allNoteCount = notes.length;
    closedNoteCount = notes.filter(n => n.closedAt).length;

    const typeAndIds = [...new Set(notes.map(n => `${n.type}-${n.typeId}`))];

    reportReviewStatistics = typeAndIds.map(string => {
      const [type, typeId] = string.split('-');
      const openedQuestionIds = notes
        .filter(n => n.type === type && n.typeId === +typeId && !n.closedAt)
        .map(n => n.typeQuestionId);
      const reviewerActionRequiredtypeQuestionIds = actionItemExistedNotes(
        notes,
        type,
        typeId,
        true,
      ).map(n => n.typeQuestionId);
      const auditorActionRequiredtypeQuestionIds = actionItemExistedNotes(
        notes,
        type,
        typeId,
        !isInternalUser,
      ).map(n => n.typeQuestionId);
      const externalUserActionRequiredtypeQuestionIds = getExternalUserActionRequiredNotes(
        notes,
        type,
        typeId,
      ).map(n => n.typeQuestionId);
      const actionNotRequiredQuestionIds = actionNotRequiredNotes(
        notes,
        type,
        typeId,
        currentUserIsJobReviewer,
      ).map(n => n.typeQuestionId);

      const noteExistedQuestionIds = () => {
        const filteredNotes = notes.filter(
          n => n.type === type && n.typeId === +typeId,
        );
        return filteredNotes.map(n => n.typeQuestionId);
      };

      let actionRequiredtypeQuestionIds = [];
      if (!isInternalUser) {
        actionRequiredtypeQuestionIds = [...externalUserActionRequiredtypeQuestionIds];
      } else if (currentUserIsJobReviewer) {
        actionRequiredtypeQuestionIds = [...reviewerActionRequiredtypeQuestionIds];
      } else if (!currentUserIsJobReviewer) {
        actionRequiredtypeQuestionIds = [...auditorActionRequiredtypeQuestionIds];
      }

      return {
        type,
        typeId,
        noteExistedQuestionIds: noteExistedQuestionIds(),
        openedQuestionIds,
        actionRequiredtypeQuestionIds,
        actionNotRequiredQuestionIds,
        reviewerActionsNeededCount:
        reviewerActionRequiredtypeQuestionIds.length,
        auditorActionsNeededCount: auditorActionRequiredtypeQuestionIds.length,
        externalActionsNeededCount: externalUserActionRequiredtypeQuestionIds.length,
      };
    });
  }

  const allReviewerActionsNeededCount =
    (reportReviewStatistics || []).reduce((s, o) => s + o.reviewerActionsNeededCount, 0);

  const allAuditorActionsNeededCount =
    (reportReviewStatistics || []).reduce((s, o) => s + o.auditorActionsNeededCount, 0);

  const allExternalActionsNeededCount =
    (reportReviewStatistics || []).reduce((s, o) => s + o.externalActionsNeededCount, 0);

  const {
    data: capFindingsData,
    loading: capFindingsLoading,
    refetch: reloadCapFindings,
    updateQuery: updateCapQueryCache,
  } = useCapFindings(id);

  const {
    data: saqJobExternalStakeholdersData,
    loading: isLoadingSaqJobExternalStakeholders,
    refetch: refetchJobExternalStakeholders,
  } = useExternalJobStakeholders(id, ServiceTypes.SAQ);

  return {
    allAuditorActionsNeededCount,
    allNoteCount,
    allReviewerActionsNeededCount,
    allExternalActionsNeededCount,
    auth,
    capFindingsData,
    capFindingsLoading,
    closedNoteCount,
    companyTypes,
    fullJobLoading,
    getResultHistories,
    isNew,
    isInternalUser,
    isReportReviewer: currentUserIsJobReviewer,
    loading: 
      jobRelationloading ||
      reportReviewDataLoading ||
      fullJobLoading ||
      companyTypesLoading ||
      !reportReviewStatistics,
    job,
    jobChecklistResults,
    jobDatasheetResults,
    isDatasheetResultsLoaded,
    jobMeta,
    jobloading,
    permissions,
    refetchJobRelation,
    refetchReportReview,
    refetchResultHistories,
    reloadCapFindings,
    reloadJob,
    reloadJobChecklistResults,
    reloadJobDatasheetResults,
    reportReviewData,
    reportReviewDataLoading,
    reportReviewStatistics,
    resultHistories,
    resultHistoriesDataLoading,
    updateCapQueryCache,
    saqJobExternalStakeholdersData,
    isLoadingSaqJobExternalStakeholders
  };
};
